import React, { useEffect, useMemo, useState } from "react";
import { styled } from "styled-components";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";

import DetailPageLayout from "../../../component/atoms/layout/detail/detailPageLayout";
import MainLayout from "../../../component/atoms/layout/mainLayout";
import PretendardText from "../../../component/atoms/text/pretendardText";
import TableTitle from "../../../component/atoms/text/table/tableTitle";
import Table from "../../../component/atoms/layout/table/table";
import Tr from "./../../../component/atoms/layout/table/tr";
import Th from "./../../../component/atoms/layout/table/th";
import Checkbox from "./../../../component/atoms/input/checkbox";
import TableText from "./../../../component/atoms/text/table/tableText";
import Td from "./../../../component/atoms/layout/table/td";
import GrayBtn from "./../../../component/atoms/button/grayBtn";
import TableTop from "./../../../component/atoms/layout/table/tableTop";
import EventBtn from "./../../../component/atoms/button/eventBtn";
import RedBtn from "./../../../component/atoms/button/redBtn";

import TableCount from "./../../../component/molecules/tableCount";
import Pagination from "./../../../component/molecules/pagination";

import Header from "../../../component/organisms/header";
import Sidebar from "../../../component/organisms/sidebar";

import Modal from "../../../component/templates/Modal";

import { route } from "./../../../router/route";
import { toastMsg } from "../../../util/toastMsg";
import { errorHandler } from "../../../util/errorHandler";
import CurriculumApi from "../../../api/curriculum.api";
import Empty from "../../../component/molecules/empty";
import scrollToTop from "../../../util/scrollToTop";

// sessionStorage 초기화
function sessionStorageInit() {
  sessionStorage.removeItem("sCheckedListStorage");
  const event = new Event("sessionStorageChange");
  window.dispatchEvent(event);
}

const KnowledgeCurriculum = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams(); // eslint-disable-line no-unused-vars
  const [isWriteAuthority, setIsWriteAuthority] = useState(false);

  // ---------- 쓰기 권한체크 ---------- //
  useEffect(() => {
    const writeMenu = sessionStorage.getItem("writeMenu");

    if (writeMenu?.includes(route.knowledge_curriculum)) {
      setIsWriteAuthority(true);
    } else {
      setIsWriteAuthority(false);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const categoryList = [
    { title: "고속부스트", value: "BOOST" },
    { title: "기초반", value: "BEGINNER" },
    { title: "중급반", value: "INTERMEDIATE" },
    { title: "고급반", value: "ADVANCED" },
    { title: "마스터", value: "MASTER" },
    { title: "코칭·특강·자문", value: "COACHING" },
    { title: "네트워킹", value: "NETWORKING" },
  ];

  const [selectedCategory, setSelectedCategory] = useState("BOOST");

  const page = useMemo(() => {
    return searchParams.get("page") ? searchParams.get("page") : 1;
  }, [searchParams]);

  const [pagination, setPagination] = useState({
    size: 10, // 한 페이지에 표시될 게시글 수
    rangeSize: 5, // 하단 페이지 사이즈
    totalCount: 0,
    totalPages: 0,
    first: false,
    last: false,
  });

  // ---------- 마운트가 되면 ---------- //
  useEffect(() => {
    return () => {
      sessionStorage.removeItem("sCheckedListStorage");
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ---------- 마운트가 되면 ---------- //
  useEffect(() => {
    setSelectedCategory(
      searchParams.get("category") ? searchParams.get("category") : "BOOST"
    );

    getCurriculumList();
  }, [location]); // eslint-disable-line react-hooks/exhaustive-deps

  const [curriculumList, setCurriculumList] = useState([]);

  const getCurriculumList = async () => {
    try {
      const data = {
        page: Number(page),
        size: Number(pagination.size),
        sort: "orderNumber",
        orderBy: "ASC",
      };

      data.curriculumType = searchParams.get("category")
        ? searchParams.get("category")
        : selectedCategory;

      const response = (await CurriculumApi.getList(data)).data.data;

      setCurriculumList(response.content);
      setPagination({
        ...pagination,
        totalCount: response.totalCount,
        totalPages: Math.ceil(response.totalCount / 10),
      });
    } catch (error) {
      errorHandler(error);
    }
  };

  const sessionStorageCheckedList = sessionStorage.getItem(
    "sCheckedListStorage"
  );
  const [checkedListStorage, setCheckedListStorage] = useState([]);
  const [buttonGroupOpen, setButtonGroupOpen] = useState(false);
  const [isAllCheck, setIsAllCheck] = useState(false);

  useEffect(() => {
    const handleStorageChange = () => {
      setCheckedListStorage(
        sessionStorageCheckedList ? JSON.parse(sessionStorageCheckedList) : []
      );
    };

    window.addEventListener("sessionStorageChange", handleStorageChange);

    return () => {
      window.removeEventListener("sessionStorageChange", handleStorageChange);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    sessionStorage.setItem(
      "sCheckedListStorage",
      JSON.stringify(checkedListStorage)
    );
  }, [page]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (curriculumList.length > 0) {
      const idsInArray1 = new Set(checkedListStorage.map((item) => item.id));

      const allIdsIncluded = curriculumList.every((item) =>
        idsInArray1.has(item.id)
      );

      setIsAllCheck(allIdsIncluded);
    }

    if (checkedListStorage.length > 0) {
      setButtonGroupOpen(true);
    } else {
      setButtonGroupOpen(false);
    }
  }, [checkedListStorage, curriculumList]); // eslint-disable-line react-hooks/exhaustive-deps

  const [deleteModalOpen, setDeleteModalOpen] = useState(false);

  const deleteHandler = async () => {
    try {
      let delIds = checkedListStorage.map((item) => item.id);

      const data = {
        ids: delIds,
        allFlag: false,
      };

      await CurriculumApi.delete(data);

      sessionStorageInit();
      setDeleteModalOpen(false);
      toast(toastMsg.delete);

      navigate(
        `${route.knowledge_curriculum}?page=1&category=${selectedCategory}`
      );
    } catch (error) {
      errorHandler(error);
    }
  };

  const orderChangeFunc = async (orderDirection) => {
    try {
      const data = {
        orderDirection: orderDirection,
        curriculumSearchDto: {
          curriculumType: selectedCategory,
        },
      };

      const response = (
        await CurriculumApi.order(checkedListStorage[0].id, data)
      ).data.data;

      sessionStorageInit();
      getCurriculumList();
      toast(toastMsg.create);
    } catch (error) {
      errorHandler(error);
    }
  };

  return (
    <>
      <Header />
      <MainLayout
        style={{
          position: "relative",
          gap: 306,
        }}
      >
        <Sidebar />

        {/* 본문 영역 */}
        <DetailPageLayout>
          <TableTitle>커리큘럼 관리</TableTitle>
          <TabWrapper>
            {categoryList.map((item, index) => {
              return (
                <Tab
                  key={index}
                  isActive={selectedCategory === item.value}
                  onClick={() => {
                    navigate(
                      `${route.knowledge_curriculum}?page=1&category=${item.value}`
                    );
                  }}
                >
                  <TabText isActive={selectedCategory === item.value}>
                    {item.title}
                  </TabText>
                </Tab>
              );
            })}
          </TabWrapper>
          {/* 테이블 상단 영역 */}
          <TableTop>
            <TableCount text={"목록"} count={pagination.totalCount} />
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 10,
              }}
            >
              {isWriteAuthority && (
                <EventBtn
                  onClick={() => navigate(route.knowledge_curriculum_create)}
                  text={"상품 추가하기"}
                />
              )}
            </div>
          </TableTop>

          {/* 버튼 그룹 */}
          {buttonGroupOpen && isWriteAuthority && (
            <div
              style={{
                width: "100%",
                height: 49,
                display: "flex",
                gap: 12,
              }}
            >
              <RedBtn
                style={{
                  backgroundColor: "#272829",
                }}
                onClick={() => {
                  setDeleteModalOpen(true);
                }}
              >
                삭제
              </RedBtn>
              {checkedListStorage.length > 1 ? null : (
                <>
                  <GrayBtn
                    onClick={() => {
                      orderChangeFunc("TOP");
                    }}
                  >
                    맨 위로
                  </GrayBtn>
                  <GrayBtn
                    onClick={() => {
                      orderChangeFunc("UP");
                    }}
                  >
                    위로
                  </GrayBtn>
                  <GrayBtn
                    onClick={() => {
                      orderChangeFunc("DOWN");
                    }}
                  >
                    아래로
                  </GrayBtn>
                  <GrayBtn
                    onClick={() => {
                      orderChangeFunc("BOTTOM");
                    }}
                  >
                    맨 아래로
                  </GrayBtn>
                </>
              )}
            </div>
          )}

          {/* 테이블 영역 */}
          <Table>
            <colgroup>
              {/* 합계: 1044px */}
              {/* 체크박스 */}
              <col width="72px" />
              {/* 상품명 */}
              <col width="972px" />
            </colgroup>
            <thead>
              <Tr style={{ height: "41px", background: "#272829" }}>
                <Th style={{ paddingLeft: "20px" }}>
                  <Checkbox
                    checked={isAllCheck}
                    onChange={(e) => {
                      if (e.target.checked) {
                        // 배열 병합 및 중복 제거
                        const mergedArray = [
                          ...curriculumList,
                          ...checkedListStorage,
                        ].reduce((acc, current) => {
                          const existingItem = acc.find(
                            (item) => item.id === current.id
                          );
                          if (!existingItem) {
                            acc.push(current);
                          }
                          return acc;
                        }, []);

                        setCheckedListStorage(mergedArray);
                      } else {
                        const idsToExclude = new Set(
                          curriculumList.map((item) => item.id)
                        );
                        const filteredArray = checkedListStorage.filter(
                          (item) => !idsToExclude.has(item.id)
                        );

                        setCheckedListStorage(filteredArray);
                      }
                    }}
                  />
                </Th>
                <Th>
                  <TableText
                    style={{
                      fontWeight: "600",
                      lineHeight: "22.75px",
                    }}
                  >
                    상품명
                  </TableText>
                </Th>
              </Tr>
            </thead>
            <tbody>
              {curriculumList.map((item, index) => {
                return (
                  <Tr
                    key={index}
                    style={{
                      height: "59px",
                      padding: "20px 0px",
                      background: "transparent",
                      borderBottom: "0.5px solid rgba(255, 255, 255, 0.12)",
                    }}
                  >
                    <Td style={{ paddingLeft: "20px" }}>
                      <Checkbox
                        checked={checkedListStorage.some(
                          (item02) => item02.id === item.id
                        )}
                        onChange={() => {
                          if (
                            checkedListStorage.length > 0 &&
                            checkedListStorage.some(
                              (item02) => item02.id === item.id
                            )
                          ) {
                            let temp = checkedListStorage.filter(
                              (item02) => item02.id !== item.id
                            );
                            setCheckedListStorage(temp);
                          } else {
                            setCheckedListStorage([
                              item,
                              ...checkedListStorage,
                            ]);
                          }
                        }}
                      />
                    </Td>
                    <Td>
                      <TableText
                        onClick={() => {
                          navigate(
                            `${route.knowledge_curriculum_edit}/${item.id}`
                          );
                          scrollToTop();
                        }}
                        style={{
                          cursor: "pointer",
                        }}
                      >
                        {item.title}
                      </TableText>
                    </Td>
                  </Tr>
                );
              })}
            </tbody>
          </Table>
          {curriculumList.length > 0 ? null : <Empty />}

          {/* 페이지네이션 영역 */}
          <Pagination
            route={route.knowledge_curriculum}
            queryString={`category=${selectedCategory}`}
            currentPage={page}
            totalPages={pagination.totalPages}
            rangeSize={pagination.rangeSize}
            first={pagination.first}
            last={pagination.last}
          />
        </DetailPageLayout>
      </MainLayout>

      {/* 삭제 모달 */}
      {deleteModalOpen && (
        <Modal
          type="negative"
          title="삭제하시겠습니까?"
          text={
            <>
              삭제된 데이터는 복구가 불가합니다.
              <br />
              그래도 삭제하시겠습니까?
            </>
          }
          setModalOpen={setDeleteModalOpen}
          onClick={deleteHandler}
        />
      )}
    </>
  );
};

export default KnowledgeCurriculum;

const TabWrapper = styled.div.attrs((props) => {})`
  width: fit-content;
  display: flex;
  gap: 10px;
  align-items: center;
  margin-bottom: 30px;
`;

const Tab = styled.div.attrs((props) => {})`
  width: fit-content;
  height: fit-content;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 19px 28px;
  border-bottom: ${(props) => (props.isActive ? "2px solid #27beff" : "none")};
  cursor: pointer;
`;

const TabText = styled(PretendardText).attrs((props) => {})`
  font-weight: 700;
  font-size: 16px;
  line-height: 25px;
  letter-spacing: -0.3%;
  color: ${(props) =>
    props.isActive ? "#27beff" : "rgba(181, 181, 181, 0.5)"};
`;
